import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PortalShell } from "@/components/PortalShell";

const $ = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const d = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU") : "—");

export default function RetailInvoices() {
  const [pending, setPending] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(null);

  const [loadError, setLoadError] = useState(false);
  const load = () => {
    Promise.all([
      api.get("/admin/retail-invoices/pending").then(({ data }) => setPending(data.partners || [])),
      api.get("/admin/retail-invoices").then(({ data }) => setInvoices(data.invoices || [])),
    ]).then(() => setLoadError(false)).catch(() => setLoadError(true));
  };
  useEffect(load, []);

  const generate = async (partnerId) => {
    try {
      const { data } = await api.post("/admin/retail-invoices/generate", { partner_id: partnerId });
      toast.success(`Invoice ${data.invoice_number} generated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const markPaid = async (inv) => {
    try {
      await api.post(`/admin/retail-invoices/${inv.id}/mark-paid`);
      toast.success("Marked paid"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark paid"); }
  };
  const pdf = async (inv) => {
    try {
      const r = await api.get(`/admin/retail-invoices/${inv.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not load the invoice PDF"); }
  };
  const overdue = (inv) => inv.status === "issued" && inv.due_at && new Date(inv.due_at) < new Date();

  return (
    <PortalShell title="Retail Invoices" subtitle="Wholesale invoicing for Retail Stockist counter bookings and kit reorders.">
    <div className="space-y-6" data-testid="retail-invoices-page">
      {loadError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="retail-invoices-error">
          Couldn't load retail invoices. <button className="underline font-semibold" onClick={load}>Retry</button>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="uninvoiced-panel">
        <h2 className="text-sm font-bold text-slate-700">Pending uninvoiced Retail charges</h2>
        <div className="mt-2 space-y-1 text-sm">
          {pending.map((p) => (
            <div key={p.partner_id} className="flex flex-wrap items-center gap-3 border-b border-slate-100 py-1.5" data-testid={`pending-partner-${p.partner_id}`}>
              <span className="font-semibold">{p.business_name || p.partner_id}</span>
              <span className="text-slate-500">{p.charges_count} charge(s)</span>
              <span>subtotal {$(p.subtotal_cents)} − credits {$(p.kit_credits_cents)}</span>
              <span className="ml-auto font-semibold">{$(p.net_cents)}</span>
              <Button size="sm" onClick={() => generate(p.partner_id)} data-testid={`generate-invoice-${p.partner_id}`}>Generate Invoice</Button>
            </div>
          ))}
          {!pending.length && <div className="text-slate-500">Nothing awaiting invoicing.</div>}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-bold text-slate-700">Invoices</h2>
        <div className="mt-2 space-y-1 text-sm">
          {invoices.map((inv) => (
            <div key={inv.id} className="border-b border-slate-100 py-1.5" data-testid={`invoice-row-${inv.invoice_number}`}>
              <div className="flex flex-wrap items-center gap-3">
                <button className="font-mono text-cyan-700 underline" onClick={() => setOpen(open === inv.id ? null : inv.id)}>{inv.invoice_number}</button>
                <span>{inv.partner_name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${overdue(inv) ? "bg-rose-100 text-rose-700" : "bg-slate-100"}`}>{overdue(inv) ? "overdue" : inv.status}</span>
                <span className="text-slate-500">issued {d(inv.issued_at)}{inv.due_at ? ` · due ${d(inv.due_at)}` : ""}</span>
                <span className="ml-auto font-semibold">{$(inv.total_cents)}</span>
                <button className="text-xs text-cyan-700 underline" onClick={() => pdf(inv)} data-testid={`invoice-pdf-${inv.invoice_number}`}>PDF</button>
                {inv.status === "issued" && <Button size="sm" variant="outline" onClick={() => markPaid(inv)} data-testid={`invoice-paid-${inv.invoice_number}`}>Mark Paid</Button>}
              </div>
              {open === inv.id && (
                <div className="mt-2 rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs" data-testid="invoice-detail">
                  {(inv.line_items || []).map((l, i) => (
                    <div key={i} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{l.booking_reference || l.booking_ref} — {l.test_name}{l.kit_code ? ` (kit ${l.kit_code})` : ""}</span>
                      <span>{$(l.wholesale_cents)}{l.kit_credit_cents ? ` − ${$(l.kit_credit_cents)} credit` : ""}</span>
                    </div>
                  ))}
                  <div className="mt-2 text-right font-bold">Subtotal {$(inv.subtotal_cents)} · Credits {$(inv.kit_credits_cents)} · Due {$(inv.total_cents)}</div>
                </div>
              )}
            </div>
          ))}
          {!invoices.length && <div className="text-slate-500">No invoices yet.</div>}
        </div>
      </div>
    </div>
    </PortalShell>
  );
}
